/**
 * Production MVP polish L1 allowlists (P1c coordinator, P2a facility lead).
 *
 * Applied after `navV2ItemIsVisible` in the sidebar only. Cmd-K, Home tiles,
 * and live RBAC stay on the entitled set. Do not hide by editing `roles: []`
 * aliases — FACILITY_MANAGER aliases include EXEC+COORDINATOR.
 */

import { REWRITTEN_HREFS } from './nav-v2';

/** Live roles whose sidebar L1 is cut in P1c. */
export const MVP_POLISH_COORDINATOR_L1_ROLES: ReadonlySet<string> = new Set([
  'COORDINATOR',
  'CASE_MANAGER',
  'TEAM_LEADER',
]);

/**
 * Live facility-lead role for P2a. REGION_MANAGER also aliases FACILITY
 * but is not this cut.
 */
export const MVP_POLISH_FACILITY_LEAD_L1_ROLES: ReadonlySet<string> = new Set(['FACILITY_MANAGER']);

/**
 * Rewritten / live hrefs that stay on coordinator L1 (and footer Help).
 * `/roster` matches `/roster/dispatch` via `rewriteMvpPolishL1Href`.
 * `/settings` hub is tenant.configure only — empty KEEP (BA P7).
 */
export const MVP_POLISH_COORDINATOR_L1_HREFS: ReadonlySet<string> = new Set([
  '/overview',
  '/clients',
  '/roster/dispatch',
  '/home-care/service-delivery',
  '/compliance/incidents',
  '/tasks',
  '/people',
  '/help',
]);

/**
 * Extra coordinator KEEP doors under a home-care (SAH) lens only. Paul ruled
 * first-hand on 3 Nov 2026 ("Add both back") after the Simply At Home
 * walkthrough, reversing the P1c FOLD for these two routes. Entitlement still
 * decides visibility: this only stops the polish cut from hiding them.
 */
export const MVP_POLISH_COORDINATOR_SAH_L1_HREFS: ReadonlySet<string> = new Set([
  '/home-care/readiness',
  '/care-plans',
]);

/**
 * Rewritten / live hrefs that stay on facility-lead L1 (and footer).
 * Do not add MAR, huddle, complaints, or `/dashboards/executive`.
 * `/settings` hub is tenant.configure only — empty KEEP for these roles (BA P7).
 */
export const MVP_POLISH_FACILITY_LEAD_L1_HREFS: ReadonlySet<string> = new Set([
  '/overview',
  '/residential',
  '/clients',
  '/roster/dispatch',
  '/compliance/incidents',
  '/tasks',
  '/people',
  '/help',
]);

/** Parent-route rewrites that NAV_V2 applies in `n()`, plus the roster live door. */
const LIVE_HREF_ALIASES: Readonly<Record<string, string>> = {
  '/roster': '/roster/dispatch',
};

export function rewriteMvpPolishL1Href(href: string): string {
  const pathname = href.split(/[?#]/)[0] || href;
  return REWRITTEN_HREFS[pathname] ?? LIVE_HREF_ALIASES[pathname] ?? pathname;
}

export function isMvpPolishCoordinatorL1Role(liveRole: string | null | undefined): boolean {
  if (!liveRole) return false;
  return MVP_POLISH_COORDINATOR_L1_ROLES.has(liveRole.toUpperCase());
}

export function isMvpPolishFacilityLeadL1Role(liveRole: string | null | undefined): boolean {
  if (!liveRole) return false;
  return MVP_POLISH_FACILITY_LEAD_L1_ROLES.has(liveRole.toUpperCase());
}

/**
 * After entitlement gates: keep the leaf only if this role is not in the
 * P1c cut, or the rewritten href is on the coordinator KEEP list.
 */
export function isMvpPolishCoordinatorL1HrefAllowed(
  href: string,
  liveRole: string | null | undefined,
  homeCareLens = false,
): boolean {
  if (!isMvpPolishCoordinatorL1Role(liveRole)) return true;
  const rewritten = rewriteMvpPolishL1Href(href);
  return (
    MVP_POLISH_COORDINATOR_L1_HREFS.has(rewritten) ||
    (homeCareLens && MVP_POLISH_COORDINATOR_SAH_L1_HREFS.has(rewritten))
  );
}

/**
 * After entitlement gates: keep the leaf only if this role is not the
 * P2a facility-lead cut, or the rewritten href is on the facility KEEP list.
 */
export function isMvpPolishFacilityLeadL1HrefAllowed(
  href: string,
  liveRole: string | null | undefined,
): boolean {
  if (!isMvpPolishFacilityLeadL1Role(liveRole)) return true;
  return MVP_POLISH_FACILITY_LEAD_L1_HREFS.has(rewriteMvpPolishL1Href(href));
}

/** Sidebar L1 + footer cut for shipped polish roles. */
export function isMvpPolishL1HrefAllowed(
  href: string,
  liveRole: string | null | undefined,
  homeCareLens = false,
): boolean {
  if (isMvpPolishCoordinatorL1Role(liveRole)) {
    return isMvpPolishCoordinatorL1HrefAllowed(href, liveRole, homeCareLens);
  }
  if (isMvpPolishFacilityLeadL1Role(liveRole)) {
    return MVP_POLISH_FACILITY_LEAD_L1_HREFS.has(rewriteMvpPolishL1Href(href));
  }
  return true;
}
